import { ContentTypeEnum } from '@elearning-platform/api-types/enums';
import { ContentsUnion, LearningElementsInterface, LearningInterface, TagInterface } from '@elearning-platform/api-types/interfaces';
import {
  Breakpoint,
  Card,
  CardGroupCard,
  CardVariant,
  CollapsibleSidePanel,
  Color,
  ContentFilter,
  ContentFilterOption,
  ContentFilterType,
  ContentTableItem,
  DatesRange,
  FullScreenAlertVariant,
  HelpBanner,
  HelpBannerType,
  useNotifications,
  UseNotifications,
  UseState,
} from '@elearning-platform/ui';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { downloadFile, getThumbnailListsAndTags } from '../../api/requests';
import { AppConfig } from '../../appConfig';
import { KnowledgeBaseViewMode, LessonStatus, QueryKey, RouteNameEnum, ViewImage } from '../../enums';
import { useAuth, useLessonCard, useRedirectTo, useScrollMemory } from '../../hooks';
import { UseAuth, UseLessonCard, UseRedirectTo } from '../../interfaces';
import { normalizeSearchValue } from '../../utils';

import { CategorySection, ViewOptions } from './components';
import { CategoryFilter, FilterName } from './enums';
import { useDrawerContent } from './hooks';
import { CategoryData, FilterableAttribute } from './interfaces';
import { filterableAttributes, filters } from './knowledgeBaseV2.consts';
import {
  BottomContainer,
  CollapsibleSidePanelCardWrapper,
  Container,
  DownloadBoxWrapper,
  MiddleContainer,
  StyledContentFilters,
  StyledDownloadBox,
  StyledEmptyPlaceholder,
} from './knowledgeBaseV2.styled';
import { mockSkeletonCard, mockSkeletonTable } from './mocks';

const KnowledgeBaseV2: React.FC = (): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const [searchValue, setSearchValue]: UseState<string> = useState<string>('');
  const [activeFilters, setActiveFilters]: UseState<Record<string, string[] | DatesRange>>
    = useState<Record<string, string[] | DatesRange>>({});
  const isSmallDesktop: boolean = useMediaQuery({ query: Breakpoint.SmallDesktop });
  const {
    getCardImage,
    itemToShowOnSidePanel,
    setItemToShowOnSidePanel,
    handleOnCardClick,
    transformThumbnailIntoCardData,
    transformThumbnailIntoTableData,
  }: UseLessonCard = useLessonCard();
  useScrollMemory(false);
  const { showFullscreenAlert, hideFullscreenAlert }: UseNotifications = useNotifications();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { appSettings }: UseAuth = useAuth();
  const viewMode: KnowledgeBaseViewMode = appSettings?.knowledgeBaseViewMode ?? KnowledgeBaseViewMode.Grid;

  const { data: lessons, isFetching, isPending, error, isError }: UseQueryResult<LearningInterface> = useQuery({
    queryKey: [QueryKey.KnowledgeBaseData],
    queryFn: (): Promise<LearningInterface> => getThumbnailListsAndTags(),
  });

  useEffect((): void => {
    if (isError) {
      showFullscreenAlert({
        title: t('competence.app.global.defaultErrorHeader'),
        description: error.message || t('competence.app.global.defaultErrorDescription'),
        variant: FullScreenAlertVariant.Dark,
        acceptButtonData: {
          label: t('competence.app.global.mainPage'),
          onClick: (): void => {
            redirect({ route: RouteNameEnum.Dashboard });
            hideFullscreenAlert();
          },
        },
      });
    }
  }, [isError, error, redirect, showFullscreenAlert, hideFullscreenAlert, t]);

  const allContents: ContentsUnion[] = useMemo(
    (): ContentsUnion[] =>
      lessons?.elements.flatMap((item: LearningElementsInterface): ContentsUnion[] => item.items) ?? [],
    [lessons]
  );
  const drawerContent: ContentsUnion | undefined = useDrawerContent(allContents);

  useEffect(
    (): void => {
      if (drawerContent && drawerContent !== itemToShowOnSidePanel) {
        setItemToShowOnSidePanel(drawerContent);
      }
    },
    [drawerContent, itemToShowOnSidePanel, setItemToShowOnSidePanel]
  );

  const filterByType: (item: ContentsUnion) => boolean = useCallback((item: ContentsUnion): boolean => {
    const typesToFilter: string[] | DatesRange = activeFilters[FilterName.Type];
    if (typesToFilter && Array.isArray(typesToFilter)) {
      if (typesToFilter.includes('favorite')) {
        return !!item.favorite;
      } else if ([ContentTypeEnum.file, ContentTypeEnum.files].includes(item.type)) {
        return typesToFilter.some((filter: string): boolean => filter === CategoryFilter.Files);
      } else if ([ContentTypeEnum.lesson, ContentTypeEnum.lessonSlideScroll].includes(item.type)) {
        return typesToFilter.some((filter: string): boolean => filter === CategoryFilter.ELearning);
      } else if ([ContentTypeEnum.video, ContentTypeEnum.contents].includes(item.type)) {
        return typesToFilter.some((filter: string): boolean => filter === CategoryFilter.Video);
      }

      return false;
    }

    return true;
  }, [activeFilters]);

  const filterByTags: (item: ContentsUnion) => boolean = useCallback(
    (item: ContentsUnion): boolean => {
      const tagsToFilter: string[] | DatesRange = activeFilters[FilterName.Tags];
      if (tagsToFilter && Array.isArray(tagsToFilter) && item.tags) {
        return item.tags.some((tag: TagInterface): boolean => !!tagsToFilter.find((item: string): boolean => item === tag.name));
      }

      return true;
    },
    [activeFilters]
  );

  const filterByAttributes: (item: ContentsUnion) => boolean = useCallback(
    (item: ContentsUnion): boolean => {
      const attributesToFilter: string[] | DatesRange = activeFilters[FilterName.Attributes];
      if (attributesToFilter && Array.isArray(attributesToFilter)) {
        return filterableAttributes.some((attr: FilterableAttribute): boolean =>
          attributesToFilter.includes(attr.value) && attr.matches(item)
        );
      }

      return true;
    },
    [activeFilters]
  );

  const filterByStatus: (item: ContentsUnion) => boolean = useCallback(
    (item: ContentsUnion): boolean => {
      const statusesToFilter: string[] | DatesRange = activeFilters[FilterName.Status];
      if (statusesToFilter
        && Array.isArray(statusesToFilter)
        && item.type !== ContentTypeEnum.contents
        && item.type !== ContentTypeEnum.file
        && item.type !== ContentTypeEnum.files
      ) {
        return !!item.statusKey && statusesToFilter.includes(item.statusKey)
          || (statusesToFilter.includes(LessonStatus.Finished) && item.statusKey === LessonStatus.ValidateTo);
      }

      return true;
    },
    [activeFilters]
  );

  const filterBySearchValue: (item: ContentsUnion) => boolean = useCallback(
    (item: ContentsUnion): boolean => !searchValue || normalizeSearchValue(item.name)
      .includes(normalizeSearchValue(searchValue)), [searchValue]);

  const filterThumbnails: (thumbnails: ContentsUnion[]) => ContentsUnion[] = useCallback((thumbnails: ContentsUnion[]): ContentsUnion[] => (
    thumbnails.filter((item: ContentsUnion): boolean => (
      filterByStatus(item)
      && filterByType(item)
      && filterByAttributes(item)
      && filterByTags(item)
      && filterBySearchValue(item)
    ))
  ), [filterByTags, filterByType, filterByAttributes, filterByStatus, filterBySearchValue]);

  const inProgressMaterials: ContentsUnion[] = useMemo((): ContentsUnion[] => {
    const inProgress: ContentsUnion[] = [];
    if (lessons) {
      lessons.elements.forEach((list: LearningElementsInterface): void => list.items.forEach((item: ContentsUnion): void => {
        if ('statusKey' in item && item.statusKey === LessonStatus.InProgress) {
          inProgress.push(item);
        }
      }));
    }

    return inProgress;
  }, [lessons]);

  const inProgressCardData: CardGroupCard[] = useMemo((): CardGroupCard[] => {
    if (isFetching || isPending) {
      return Array.from({ length: 6 }, (): CardGroupCard => mockSkeletonCard);
    }

    return filterThumbnails(inProgressMaterials).map(transformThumbnailIntoCardData);
  }, [isFetching, isPending, inProgressMaterials, filterThumbnails, transformThumbnailIntoCardData]);

  const inProgressTableData: ContentTableItem[] = useMemo((): ContentTableItem[] => {
    if (isFetching || isPending) {
      return Array.from({ length: 4 }, (): ContentTableItem => mockSkeletonTable);
    }

    return filterThumbnails(inProgressMaterials).map(transformThumbnailIntoTableData);
  }, [isFetching, isPending, inProgressMaterials, filterThumbnails, transformThumbnailIntoTableData]);

  const categoryData: CategoryData[] | undefined = useMemo((): CategoryData[] | undefined => (
    lessons?.elements
      .map((thumbnailList: LearningElementsInterface): CategoryData => {
        const filteredItems: ContentsUnion[] = filterThumbnails(thumbnailList.items);

        return {
          title: thumbnailList.title,
          cardData: filteredItems.map(transformThumbnailIntoCardData),
          tableData: filteredItems.map(transformThumbnailIntoTableData),
          listLength: filteredItems.length,
        };
      })
      .filter((element: CategoryData): boolean => !!element.listLength)
  ), [lessons, filterThumbnails, transformThumbnailIntoCardData, transformThumbnailIntoTableData]);

  return (
    <>
      <Container>
        {!!lessons && (
          <StyledContentFilters
            filters={[
              ...filters.map((item: ContentFilter): ContentFilter => (
                item.type === ContentFilterType.DatesRange ? {
                  ...item,
                  label: t(item.label),
                } : {
                  ...item,
                  label: t(item.label),
                  options: item.options.map((option: ContentFilterOption): ContentFilterOption => ({
                    ...option,
                    label: t(option.label),
                  })),
                }
              )),
              ...lessons.tags ? [{
                options: lessons.tags.map((tag: TagInterface): ContentFilterOption => ({ value: tag.name, label: tag.name })),
                name: FilterName.Tags,
                label: t('competence.app.knowledgeBaseV2.filters.tags'),
                type: ContentFilterType.Tag as const,
                isFullWidth: true,
              }] : [],
            ]}
            initialSelectedFilters={activeFilters}
            onFiltersChange={setActiveFilters}
            onSearchValueChange={setSearchValue}
            searchPlaceholder={t('competence.app.knowledgeBaseV2.searchPlaceholder')}
          >
            <ViewOptions />
          </StyledContentFilters>
        )}
        <MiddleContainer $isListViewMode={viewMode === KnowledgeBaseViewMode.List}>
          <CategorySection
            cardData={inProgressCardData}
            categoryTitle={t('competence.app.knowledgeBaseV2.inProgressSectionTitle')}
            isListMode={viewMode === KnowledgeBaseViewMode.List}
            isLoading={isFetching || isPending}
            listLength={filterThumbnails(inProgressMaterials).length}
            tableData={inProgressTableData}
            viewMode={viewMode}
            withVerticalAlignment
          />
        </MiddleContainer>
        <BottomContainer>
          {categoryData?.length ? (
            categoryData.map((category: CategoryData): JSX.Element | false => (
              <CategorySection
                cardData={category.cardData}
                categoryTitle={category.title}
                emptyGridColors={{ bgColor: Color.White }}
                isListMode={viewMode === KnowledgeBaseViewMode.List}
                isLoading={isFetching || isPending}
                key={category.title}
                listLength={category.listLength}
                tableData={category.tableData}
                viewMode={viewMode}
              />
            ))
          ) : (
            <StyledEmptyPlaceholder
              colorsData={{ bgColor: Color.White }}
              text={t('competence.app.knowledgeBaseV2.categorySection.emptyListMessage')}
            />
          )}
        </BottomContainer>
        <HelpBanner
          data={{
            type: HelpBannerType.Contact,
            bgImage: isSmallDesktop
              ? ViewImage.CommonHelpBannerBg
              : ViewImage.CommonHelpBannerMobileBg,
            text: t('competence.app.global.helpBanner.needHelp'),
            buttonData: {
              label: t('competence.app.global.helpBanner.help'),
              externalUrl: `mailto:${AppConfig.platformContactEmail}`,
            },
          }}
        />
      </Container>
      <CollapsibleSidePanel
        isOpen={!!drawerContent}
        onClose={(): void => redirect({ route: RouteNameEnum.KnowledgeBase, replace: true })}
        title={itemToShowOnSidePanel
          ? t(
            `competence.app.knowledgeBaseV2.sidePanelTitle.${itemToShowOnSidePanel.type === ContentTypeEnum.files ? 'files' : 'videos'}`,
            { title: itemToShowOnSidePanel.name }
          )
          : ''
        }
      >
        {!!(itemToShowOnSidePanel && 'items' in itemToShowOnSidePanel) && itemToShowOnSidePanel.items.map(
          (item: ContentsUnion): JSX.Element => (
            item.type === ContentTypeEnum.file
              ? (
                  <DownloadBoxWrapper key={item.id}>
                    <StyledDownloadBox
                      image={getCardImage(item)}
                      linkData={{
                        label: item.name,
                        action: (): Promise<AxiosResponse<void>> => downloadFile(item.id),
                      }}
                    />
                  </DownloadBoxWrapper>
                )
              : (
                  <CollapsibleSidePanelCardWrapper key={item.id} onClick={(): void => handleOnCardClick(item)}>
                    <Card
                      category={item.category}
                      image={getCardImage(item)}
                      title={item.name}
                      variant={CardVariant.Horizontal}
                    />
                  </CollapsibleSidePanelCardWrapper>
                )
          )
        )}
      </CollapsibleSidePanel>
    </>
  );
};

export default KnowledgeBaseV2;
